import {
  ArrowRight,
  Briefcase,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Globe,
  MessageCircle,
  PlusCircle,
  Search,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useWhatsApp } from "@/lib/siteSettings";
import { type PublicListing } from "@/lib/listings";
import { ListingCard } from "@/components/ListingCard";
import { SEO, orgJsonLd } from "@/lib/seo";

type CardSpec = {
  key: "request" | "submit" | "wanted";
  to: string;
  Icon: typeof ClipboardList;
  primary: boolean;
};

const CARDS: CardSpec[] = [
  { key: "request", to: "/request", Icon: ClipboardList, primary: true },
  { key: "submit", to: "/submit", Icon: PlusCircle, primary: true },
  { key: "wanted", to: "/wanted", Icon: Search, primary: false },
];

const TRUST = [
  { key: "verified", Icon: CheckCircle },
  { key: "quickResponse", Icon: Zap },
  { key: "personal", Icon: CheckCircle },
  { key: "languages", Icon: Globe },
] as const;

const Index = () => {
  const { t, i18n } = useTranslation();
  const { buildWaUrl } = useWhatsApp();
  const [featured, setFeatured] = useState<PublicListing[]>([]);
  const isRtl = i18n.dir() === "rtl";
  const StripChevron = isRtl ? ChevronLeft : ChevronRight;

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase
        .from("public_listings")
        .select("*, listing_images(*)")
        .eq("is_featured", true)
        .order("created_at", { ascending: false })
        .limit(6);
      setFeatured((data ?? []) as PublicListing[]);
    };
    void load();
  }, []);

  const ctaWaUrl = buildWaUrl(t("home.ctaCard.whatsappMessage"));

  return (
    <div>
      <SEO title={t("seo.home.title")} description={t("seo.home.description")} path="/" jsonLd={orgJsonLd()} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-hero-gradient">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/60 px-4 py-2 text-sm font-semibold text-primary backdrop-blur">
              {t("home.badge")}
            </div>
            <h1 className="text-balance text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              {t("home.heroTitle")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
              {t("home.heroSubtitle")}
            </p>
          </div>

          {/* 2x2 action cards */}
          <div className="mx-auto mt-8 grid w-full max-w-2xl grid-cols-2 gap-3 sm:gap-4">
            {/* Wayfinding labels */}
            <div className="flex flex-col items-center justify-end gap-0.5 pt-2 text-center">
              <span className="text-sm font-medium text-primary">{t("home.hero.card.request.wayfinding")}</span>
              <ChevronDown className="h-4 w-4 text-primary" aria-hidden="true" />
            </div>
            <div className="flex flex-col items-center justify-end gap-0.5 pt-2 text-center">
              <span className="text-sm font-medium text-primary">{t("home.hero.card.submit.wayfinding")}</span>
              <ChevronDown className="h-4 w-4 text-primary" aria-hidden="true" />
            </div>

            {CARDS.map(({ key, to, Icon, primary }) => {
              const isWanted = key === "wanted";
              return (
                <Link
                  key={key}
                  to={to}
                  className={`group flex flex-col gap-2 rounded-xl border bg-card transition-all duration-200 ease-out hover:-translate-y-0.5 ${
                    isWanted
                      ? "col-span-2 min-h-[110px] border-primary/20 border-t-4 border-t-primary bg-gradient-to-br from-card to-primary/5 p-5 shadow-lg hover:border-primary/40 hover:shadow-premium sm:min-h-[130px] sm:p-6"
                      : `min-h-[100px] border-primary/10 p-4 sm:min-h-[110px] sm:p-5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 ${primary ? "border-t-2 border-t-primary/30" : ""}`
                  }`}
                >
                  <Icon
                    className={`${
                      isWanted
                        ? "h-6 w-6 text-primary sm:h-7 sm:w-7"
                        : `h-5 w-5 sm:h-6 sm:w-6 ${primary ? "text-primary" : "text-muted-foreground"}`
                    }`}
                    aria-hidden="true"
                  />
                  <div>
                    <p className={`${isWanted ? "text-lg font-black sm:text-xl" : "text-base font-bold"} text-foreground`}>
                      {t(`home.hero.card.${key}.title`)}
                    </p>
                    <p className={`mt-0.5 ${isWanted ? "text-sm sm:text-base" : "text-sm"} text-muted-foreground`}>
                      {t(`home.hero.card.${key}.subtitle`)}
                    </p>
                  </div>
                </Link>
              );
            })}

            {/* Services strip */}
            <Link
              to="/services"
              className="col-span-2 flex flex-col gap-2 rounded-xl border border-primary/20 bg-gradient-to-br from-card to-card/80 p-5 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/10 group"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-base font-bold text-foreground">
                  <Briefcase className="h-4 w-4 text-primary" aria-hidden="true" /> {t("home.servicesStrip.title")}
                </span>
                <StripChevron className="h-5 w-5 text-muted-foreground transition-transform group-hover:-translate-x-0 rtl:group-hover:translate-x-0" />
              </div>
              <p className="text-sm text-muted-foreground">{t("home.servicesStrip.list")}</p>
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST INDICATORS */}
      <section className="border-y border-primary/10 bg-card/50 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-5 md:grid-cols-4">
          {TRUST.map(({ key, Icon }) => (
            <div key={key} className="flex flex-col items-center gap-2 text-center">
              <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
              <p className="text-sm font-bold text-foreground">{t(`home.trust.${key}.title`)}</p>
              <p className="text-xs leading-5 text-muted-foreground">
                {t(`home.trust.${key}.desc`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="bg-surface px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex items-end justify-between">
              <h2 className="text-3xl font-black">{t("home.featured")}</h2>
              <Link to="/listings" className="text-sm font-bold text-primary hover:underline">
                {t("home.viewAll")} →
              </Link>
            </div>
            <div className="grid gap-3 md:grid-cols-2 md:gap-4 xl:grid-cols-3">
              {featured.map((l) => (
                <ListingCard key={l.id} listing={l} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BOTTOM CTA */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl rounded-2xl border border-primary/20 bg-card p-8 text-center shadow-premium">
          <h2 className="text-2xl font-black">{t("home.ctaCard.title")}</h2>
          <p className="mt-2 text-muted-foreground">{t("home.ctaCard.description")}</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/request"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-bold text-primary-foreground shadow-premium transition hover:scale-[1.02]"
            >
              {t("home.ctaCard.requestButton")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
            <a
              href={ctaWaUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-border bg-card px-6 py-3 font-bold text-foreground transition hover:bg-secondary"
            >
              <MessageCircle className="h-4 w-4" /> {t("home.ctaCard.whatsappButton")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
